import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Accordion,
  AccordionItem,
  Button,
  Card,
  CardBody,
  CardHeader,
  Chip,
  Input,
  Select,
  SelectItem,
  Spinner,
  Tab,
  Tabs,
  Textarea,
} from '@heroui/react'

import { useGifs } from '../../gifs/hooks'
import { copyToClipboard, getGif } from '../../gifs/services'
import {
  gifDisplayTitle,
  gifDisplayChips,
  gifScenarioExamples,
} from '../../gifs/types'
import type { Gif } from '../../gifs/types'
import {
  draftFixtureMeta,
  getRecommendations,
} from '../../recommendations/services/recommendationService'
import { useToast } from '../../../hooks'
import {
  ExplicitRating,
  type DraftFixtureMetaRequest,
  type FixtureNoteReaction,
  type FixtureNoteStanceReaction,
  type RecommendationCandidate,
} from '../../../../functions/src/shared/types/gif'
import { RECOMMENDATION_PRESETS } from '../../../../functions/src/modules/recommendations/config/RecommendationConfig'
import type { FixtureMeta } from '../../../../functions/tests/eval/recommendations/types'
import { BridgeUnavailableError, saveEvalFixture } from '../services'

import { GifThumbnail } from './GifThumbnail'
import { FixtureLibrary } from './FixtureLibrary'
import { FixtureTriageInbox } from './FixtureTriageInbox'
import { JudgeCalibration } from './JudgeCalibration'
import { FixtureReactionPass } from './FixtureReactionPass'
import {
  EMPTY_FORM,
  fixtureToForm,
  formToFixture,
  serializeFixture,
  type FixtureMetaForm,
} from './fixtureForm'

// Order matters: traps + forbidden are the hard gates; known-good is an
// optional legacy diagnostic that never gates.
type Bucket = 'traps' | 'forbidden' | 'good'
type View = 'library' | 'builder' | 'triage' | 'calibration'
/** Form fields the LLM drafter can propose (and mark as an unapproved draft). */
type DraftField = 'note' | 'slug' | 'description' | 'tags'

const BUCKETS: Bucket[] = ['traps', 'forbidden', 'good']

const BUCKET_LABELS: Record<Bucket, string> = {
  traps: 'Traps (tempting wrong answers — hard gate)',
  forbidden: 'Forbidden (never appropriate — hard gate)',
  good: 'Known-good (diagnostic; star ★ for the gold hard tier)',
}

const BUCKET_COLORS: Record<Bucket, 'success' | 'warning' | 'danger'> = {
  traps: 'warning',
  forbidden: 'danger',
  good: 'success',
}

const BROWSE_INITIAL = 24
const BROWSE_PAGE = 24

const PRESET_NAMES = Object.keys(RECOMMENDATION_PRESETS)
const DEFAULT_PRESET = 'CLAUDE_SONNET_4_6'

function gifMatchesQuery(gif: Gif, q: string): boolean {
  if (!q) return true
  const needle = q.toLowerCase()
  const haystack = [
    gif.id,
    gifDisplayTitle(gif),
    gif.reference.source ?? '',
    ...(gif.reference.keywords ?? []),
    ...gifDisplayChips(gif),
    ...gifScenarioExamples(gif),
  ]
    .join(' ')
    .toLowerCase()
  return haystack.includes(needle)
}

/**
 * A small numbered badge that labels each step of the authoring procedure so the
 * page reads top-to-bottom. `done` fills it in; otherwise it stays an outline.
 */
function StepBadge({ n, done }: { n: number; done?: boolean }) {
  return (
    <span
      className={`inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
        done
          ? 'bg-primary text-primary-foreground'
          : 'border-2 border-default-300 text-default-500 dark:border-default-100/40'
      }`}
    >
      {done ? '✓' : n}
    </span>
  )
}

export function EvalFixtureBuilder() {
  const toast = useToast()
  const { gifs, loading: gifsLoading, error: gifsError } = useGifs()

  const [view, setView] = useState<View>('library')

  const [form, setForm] = useState<FixtureMetaForm>(EMPTY_FORM)
  const [tagInput, setTagInput] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [browseLimit, setBrowseLimit] = useState(BROWSE_INITIAL)

  const [trapIds, setTrapIds] = useState<string[]>([])
  const [forbiddenIds, setForbiddenIds] = useState<string[]>([])
  const [goodIds, setGoodIds] = useState<string[]>([])
  // Gold tier — a curator-only HARD subset of good. A gold id is ALWAYS also
  // good; toggling gold on a gif that isn't good yet also marks it good.
  const [goldIds, setGoldIds] = useState<string[]>([])

  // Editing an existing fixture: original request payload (to preserve fields
  // the form doesn't surface) + the JSON snapshot at load/save for dirty state.
  const [editingSlug, setEditingSlug] = useState<string | null>(null)
  const [originalRequest, setOriginalRequest] = useState<
    FixtureMeta['request'] | undefined
  >(undefined)
  const [savedSnapshot, setSavedSnapshot] = useState<string | null>(null)

  // Ids in a bucket that no longer resolve against Firestore (re-seed drift).
  const [deadIds, setDeadIds] = useState<Set<string>>(new Set())
  const [resolvingIds, setResolvingIds] = useState(false)

  const [recommending, setRecommending] = useState(false)
  const [recommendations, setRecommendations] = useState<
    RecommendationCandidate[] | null
  >(null)
  const [recommendError, setRecommendError] = useState<string | null>(null)
  const [preset, setPreset] = useState<string>(DEFAULT_PRESET)
  const [saving, setSaving] = useState(false)

  // Which form fields currently hold an unapproved LLM draft. A field enters
  // this set when the drafter fills it and leaves it on the author's first
  // manual edit — the human edit is what makes each field canon. The note may be
  // re-drafted while it's still an unedited draft or empty; the other fields are
  // never overwritten once the human has typed a non-empty value.
  const [draftFields, setDraftFields] = useState<Set<DraftField>>(new Set())
  const [drafting, setDrafting] = useState(false)

  const filteredGifs = useMemo(() => {
    return gifs.filter((g) => gifMatchesQuery(g, searchQuery.trim()))
  }, [gifs, searchQuery])

  const visibleGifs = filteredGifs.slice(0, browseLimit)

  const gifById = useMemo(() => {
    const m = new Map<string, Gif>()
    for (const g of gifs) m.set(g.id, g)
    return m
  }, [gifs])

  const currentFixture = useMemo(
    () =>
      formToFixture(
        form,
        {
          traps: trapIds,
          forbidden: forbiddenIds,
          good: goodIds,
          gold: goldIds,
        },
        originalRequest
      ),
    [form, trapIds, forbiddenIds, goodIds, goldIds, originalRequest]
  )
  const fixtureJson = useMemo(
    () => serializeFixture(currentFixture),
    [currentFixture]
  )

  const isDirty = savedSnapshot !== null && fixtureJson !== savedSnapshot
  const isNew = editingSlug === null

  // Workflow progress readout — derived purely from existing state, no new
  // business logic. Three glanceable steps mirroring the authoring procedure.
  const bucketTotal = trapIds.length + forbiddenIds.length + goodIds.length
  const scenarioSet =
    !!form.conversationSnippet.trim() || !!form.userIntent.trim()
  const noteDrafted = !!form.note.trim()
  // A note the human has taken ownership of (edited or hand-written, not a live
  // draft) is "approved"; a live LLM draft is still pending your edit.
  const noteApproved = noteDrafted && !draftFields.has('note')

  // Resolve bucket ids against Firestore to badge dead ones. Runs when the set
  // of bucketed ids changes (e.g. after opening a fixture).
  // Gold ⊆ good, so goldIds add nothing new here — but include them defensively
  // so an orphan gold id (not yet in good) still gets a dead-id resolution.
  const bucketedKey = [
    ...new Set([...trapIds, ...forbiddenIds, ...goodIds, ...goldIds]),
  ].join(',')
  useEffect(() => {
    const ids = bucketedKey ? bucketedKey.split(',') : []
    if (ids.length === 0) {
      setDeadIds(new Set())
      return
    }
    let cancelled = false
    setResolvingIds(true)
    ;(async () => {
      const dead = new Set<string>()
      await Promise.all(
        ids.map(async (id) => {
          // Fast path: already in the loaded library snapshot.
          if (gifById.has(id)) return
          try {
            const gif = await getGif(id)
            if (!gif) dead.add(id)
          } catch {
            dead.add(id)
          }
        })
      )
      if (!cancelled) {
        setDeadIds(dead)
        setResolvingIds(false)
      }
    })()
    return () => {
      cancelled = true
    }
    // gifById intentionally omitted: it changes identity on every gifs update;
    // the id membership check inside is enough and re-runs on bucket changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bucketedKey])

  const assignToBucket = useCallback((gifId: string, bucket: Bucket) => {
    setTrapIds((prev) =>
      bucket === 'traps'
        ? prev.includes(gifId)
          ? prev
          : [...prev, gifId]
        : prev.filter((id) => id !== gifId)
    )
    setForbiddenIds((prev) =>
      bucket === 'forbidden'
        ? prev.includes(gifId)
          ? prev
          : [...prev, gifId]
        : prev.filter((id) => id !== gifId)
    )
    setGoodIds((prev) =>
      bucket === 'good'
        ? prev.includes(gifId)
          ? prev
          : [...prev, gifId]
        : prev.filter((id) => id !== gifId)
    )
    // Gold is a subset of good — moving a gif out of `good` (to trap/forbidden)
    // must also strip its gold star.
    if (bucket !== 'good') {
      setGoldIds((prev) => prev.filter((id) => id !== gifId))
    }
  }, [])

  const removeFromBuckets = useCallback((gifId: string) => {
    setTrapIds((prev) => prev.filter((id) => id !== gifId))
    setForbiddenIds((prev) => prev.filter((id) => id !== gifId))
    setGoodIds((prev) => prev.filter((id) => id !== gifId))
    setGoldIds((prev) => prev.filter((id) => id !== gifId))
  }, [])

  // Toggle the gold star on a gif. Gold is HARD and always implies good, so
  // starring a not-yet-good gif also marks it good; un-starring leaves it good.
  const toggleGold = useCallback((gifId: string) => {
    setGoldIds((prev) =>
      prev.includes(gifId)
        ? prev.filter((id) => id !== gifId)
        : [...prev, gifId]
    )
    // Starring implies good — ensure the gif is good and out of trap/forbidden.
    setGoodIds((prev) => (prev.includes(gifId) ? prev : [...prev, gifId]))
    setTrapIds((prev) => prev.filter((id) => id !== gifId))
    setForbiddenIds((prev) => prev.filter((id) => id !== gifId))
  }, [])

  const currentBucket = (gifId: string): Bucket | null => {
    if (trapIds.includes(gifId)) return 'traps'
    if (forbiddenIds.includes(gifId)) return 'forbidden'
    if (goodIds.includes(gifId)) return 'good'
    return null
  }

  const loadFixtureIntoBuilder = useCallback((meta: FixtureMeta) => {
    const { form: nextForm, buckets } = fixtureToForm(meta)
    setForm(nextForm)
    setTrapIds(buckets.traps)
    setForbiddenIds(buckets.forbidden)
    setGoodIds(buckets.good)
    setGoldIds(buckets.gold)
    setEditingSlug(meta.id)
    setOriginalRequest(meta.request)
    setSavedSnapshot(serializeFixture(meta))
    setRecommendations(null)
    setRecommendError(null)
    setDraftFields(new Set())
    setSearchQuery('')
    setBrowseLimit(BROWSE_INITIAL)
    setView('builder')
  }, [])

  const startNewFixture = useCallback(() => {
    setForm(EMPTY_FORM)
    setTrapIds([])
    setForbiddenIds([])
    setGoodIds([])
    setGoldIds([])
    setEditingSlug(null)
    setOriginalRequest(undefined)
    // A blank fixture has a snapshot too, so "dirty" tracks edits from blank.
    setSavedSnapshot(
      serializeFixture(
        formToFixture(EMPTY_FORM, {
          traps: [],
          forbidden: [],
          good: [],
          gold: [],
        })
      )
    )
    setRecommendations(null)
    setRecommendError(null)
    setDraftFields(new Set())
    setSearchQuery('')
    setBrowseLimit(BROWSE_INITIAL)
    setView('builder')
  }, [])

  // Clear a field's draft flag — called on the author's first manual edit of it.
  const clearDraftField = useCallback((field: DraftField) => {
    setDraftFields((prev) => {
      if (!prev.has(field)) return prev
      const next = new Set(prev)
      next.delete(field)
      return next
    })
  }, [])

  // Draft slug/description/tags/note from the current conversation + buckets
  // (plus any reactions from the Explore & react pass). Fills empty fields and
  // marks them as drafts; NEVER overwrites a field the human already typed a
  // non-empty value into. The note is the exception: it may be overwritten while
  // it's still an unedited draft or empty (matching the existing note behavior),
  // and when a non-empty note survives it's sent as `existingNote` so the call
  // runs in revision mode.
  const runDraft = useCallback(
    async (extra?: {
      reactions?: FixtureNoteReaction[]
      stanceReactions?: FixtureNoteStanceReaction[]
    }) => {
      const context: DraftFixtureMetaRequest['context'] = {
        ...(form.conversationSnippet.trim()
          ? { conversationSnippet: form.conversationSnippet.trim() }
          : {}),
        ...(form.userIntent.trim()
          ? { userIntent: form.userIntent.trim() }
          : {}),
      }
      if (!context.conversationSnippet && !context.userIntent) {
        toast.error('Add a conversation snippet or user intent first')
        return
      }

      const buckets: DraftFixtureMetaRequest['buckets'] = [
        ...trapIds.map((gifId) => ({ gifId, bucket: 'trap' as const })),
        ...forbiddenIds.map((gifId) => ({
          gifId,
          bucket: 'forbidden' as const,
        })),
        ...goodIds.map((gifId) => ({ gifId, bucket: 'good' as const })),
      ]
      const reactions = extra?.reactions ?? []
      if (buckets.length === 0 && reactions.length === 0 && !form.note.trim()) {
        toast.error(
          'Bucket at least one gif (trap / good / forbidden) or react to one first'
        )
        return
      }

      // The note can only be revised, not overwritten, once the human has edited
      // it — i.e. it holds a non-empty value that is NOT a live draft.
      const noteIsEdited =
        form.note.trim().length > 0 && !draftFields.has('note')

      setDrafting(true)
      try {
        const meta = await draftFixtureMeta(context, {
          ...(buckets.length > 0 ? { buckets } : {}),
          ...(reactions.length > 0 ? { reactions } : {}),
          ...(extra?.stanceReactions && extra.stanceReactions.length > 0
            ? { stanceReactions: extra.stanceReactions }
            : {}),
          ...(noteIsEdited ? { existingNote: form.note.trim() } : {}),
        })

        // Decide which fields to fill against the current form snapshot — pure,
        // so the state updaters below stay side-effect free (StrictMode-safe).
        // Note: overwrite only when empty or still an unedited draft.
        // slug/description/tags: fill only when the human left them empty.
        const patch: Partial<FixtureMetaForm> = {}
        const filled: DraftField[] = []
        if (
          meta.note.trim() &&
          (!form.note.trim() || draftFields.has('note'))
        ) {
          patch.note = meta.note.trim()
          filled.push('note')
        }
        if (meta.slug.trim() && !form.slug.trim() && isNew) {
          patch.slug = meta.slug.trim()
          filled.push('slug')
        }
        if (meta.description.trim() && !form.description.trim()) {
          patch.description = meta.description.trim()
          filled.push('description')
        }
        if (meta.tags.length > 0 && form.tags.length === 0) {
          patch.tags = meta.tags
          filled.push('tags')
        }

        if (filled.length > 0) {
          setForm((f) => ({ ...f, ...patch }))
          setDraftFields((prev) => {
            const nextSet = new Set(prev)
            for (const field of filled) nextSet.add(field)
            return nextSet
          })
        }

        if (filled.includes('note')) {
          // Defer focus until the textarea has the new value painted.
          requestAnimationFrame(() => {
            const el = document.getElementById('fixture-note-textarea')
            if (el instanceof HTMLTextAreaElement) {
              el.focus()
              el.setSelectionRange(el.value.length, el.value.length)
            }
          })
        }

        if (filled.length === 0) {
          toast.warning(
            'Nothing filled — every field already holds your own value. Clear a field to let the draft fill it.'
          )
        } else {
          toast.success(
            `${noteIsEdited ? 'Revised' : 'Drafted'} ${filled.join(', ')} — edit to approve`
          )
        }
      } catch (err) {
        toast.error(
          `Draft failed: ${err instanceof Error ? err.message : String(err)}`
        )
      } finally {
        setDrafting(false)
      }
    },
    [
      form.conversationSnippet,
      form.userIntent,
      form.note,
      form.slug,
      form.description,
      form.tags,
      trapIds,
      forbiddenIds,
      goodIds,
      draftFields,
      isNew,
      toast,
    ]
  )

  const handleAddTag = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      const tag = tagInput.trim()
      if (tag && !form.tags.includes(tag)) {
        // Manually adding a tag is an edit — the tag set is now the human's.
        clearDraftField('tags')
        setForm((f) => ({ ...f, tags: [...f.tags, tag] }))
      }
      setTagInput('')
    }
  }

  const handleRunRecommender = async () => {
    if (!form.conversationSnippet.trim() && !form.userIntent.trim()) {
      toast.error('Add a conversation snippet or user intent first')
      return
    }
    setRecommending(true)
    setRecommendError(null)
    setRecommendations(null)
    try {
      const result = await getRecommendations(
        form.conversationSnippet.trim() || undefined,
        form.userIntent.trim() || undefined,
        undefined,
        ExplicitRating.PG_13,
        { preset }
      )
      setRecommendations(result.recommendations)
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      setRecommendError(msg)
      toast.error(`Recommender failed: ${msg}`)
    } finally {
      setRecommending(false)
    }
  }

  const handleSave = async () => {
    const slug = (editingSlug ?? form.slug).trim()
    if (!slug) {
      toast.error('Set a slug before saving')
      return
    }
    if (!/^[a-z0-9-]+$/.test(slug)) {
      toast.error('Slug must be lowercase letters, numbers, and dashes only')
      return
    }
    if (!form.note.trim()) {
      toast.error(
        'Write a note (the viable moves) before saving — the judge scores boards against it'
      )
      return
    }
    setSaving(true)
    try {
      await saveEvalFixture(slug, currentFixture)
      setEditingSlug(slug)
      setSavedSnapshot(fixtureJson)
      toast.success(`Saved fixtures/${slug}/meta.json`)
    } catch (err) {
      if (err instanceof BridgeUnavailableError) {
        toast.error(
          'Fixture file access requires the dev server — use Copy/Download instead'
        )
      } else {
        toast.error(
          `Save failed: ${err instanceof Error ? err.message : String(err)}`
        )
      }
    } finally {
      setSaving(false)
    }
  }

  const handleCopyJson = async () => {
    const ok = await copyToClipboard(fixtureJson)
    if (ok) toast.success('Fixture JSON copied to clipboard')
    else toast.error('Failed to copy')
  }

  const handleDownloadJson = () => {
    const slug = (editingSlug ?? form.slug).trim() || 'NN-untitled-fixture'
    const blob = new Blob([fixtureJson], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${slug}.meta.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
    toast.success(`Downloaded ${slug}.meta.json — save into fixtures/${slug}/`)
  }

  const handleBackToLibrary = () => {
    if (isDirty && !confirm('Discard unsaved changes and return to library?')) {
      return
    }
    setView('library')
  }

  const renderBucketButtons = (gifId: string) => {
    const cur = currentBucket(gifId)
    const isGold = goldIds.includes(gifId)
    return (
      <div className="flex gap-1 mt-2 flex-wrap">
        {BUCKETS.map((b) => (
          <Button
            key={b}
            size="sm"
            variant={cur === b ? 'solid' : 'flat'}
            color={BUCKET_COLORS[b]}
            onPress={() => {
              if (cur === b) removeFromBuckets(gifId)
              else assignToBucket(gifId, b)
            }}
          >
            {cur === b ? `✓ ${b}` : `+ ${b}`}
          </Button>
        ))}
        {/* Gold star — the curator's HARD tier. Starring implies good, so it's
            shown once a gif is good (or already gold). */}
        {(cur === 'good' || isGold) && (
          <Button
            size="sm"
            variant={isGold ? 'solid' : 'flat'}
            color="warning"
            onPress={() => toggleGold(gifId)}
            title="Gold: you'd be disappointed not to see this (hard signal)"
          >
            {isGold ? '★ gold' : '☆ gold'}
          </Button>
        )}
        <Button
          size="sm"
          variant="light"
          onPress={async () => {
            const ok = await copyToClipboard(gifId)
            if (ok) toast.success(`Copied ID: ${gifId}`)
          }}
        >
          Copy ID
        </Button>
      </div>
    )
  }

  const renderBrowseCard = (gif: Gif) => {
    const cur = currentBucket(gif.id)
    return (
      <Card
        key={gif.id}
        className={
          cur
            ? `border-2 border-${BUCKET_COLORS[cur]} dark:border-${BUCKET_COLORS[cur]}`
            : ''
        }
      >
        <CardBody className="p-3">
          <div className="flex gap-3">
            <GifThumbnail gif={gif} className="w-24 h-24 rounded-lg" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium line-clamp-2">
                {gifDisplayTitle(gif)}
              </p>
              <p className="text-xs text-default-500 mt-1">
                {gif.reference.source ?? '—'}
              </p>
              <div className="flex flex-wrap gap-1 mt-1">
                {gifDisplayChips(gif)
                  .slice(0, 3)
                  .map((t) => (
                    <Chip key={t} size="sm" variant="flat" className="text-xs">
                      {t}
                    </Chip>
                  ))}
              </div>
              <p className="font-mono text-[10px] text-default-400 mt-1 truncate">
                {gif.id}
              </p>
            </div>
          </div>
          {renderBucketButtons(gif.id)}
        </CardBody>
      </Card>
    )
  }

  const renderBucketColumn = (bucket: Bucket, ids: string[]) => (
    <Card key={bucket}>
      <CardHeader className="pb-2">
        <div className="w-full flex items-center justify-between">
          <span className="font-semibold text-sm">{BUCKET_LABELS[bucket]}</span>
          <Chip size="sm" color={BUCKET_COLORS[bucket]} variant="flat">
            {ids.length}
          </Chip>
        </div>
      </CardHeader>
      <CardBody className="pt-0 space-y-2">
        {ids.length === 0 ? (
          <p className="text-xs text-default-400">No GIFs added yet.</p>
        ) : (
          ids.map((id) => {
            const gif = gifById.get(id)
            const isDead = deadIds.has(id)
            const isGold = bucket === 'good' && goldIds.includes(id)
            return (
              <div
                key={id}
                className={`flex gap-2 items-center p-2 rounded ${
                  isDead
                    ? 'bg-danger-50 dark:bg-danger-950/30 border border-danger-200 dark:border-danger-800'
                    : isGold
                      ? 'bg-warning-50 dark:bg-warning-950/30 border border-warning-300 dark:border-warning-800'
                      : 'bg-default-50 dark:bg-default-100/10'
                }`}
              >
                {gif ? (
                  <GifThumbnail
                    gif={gif}
                    className="w-12 h-12 rounded flex-shrink-0"
                  />
                ) : (
                  <div className="w-12 h-12 rounded bg-default-200 flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium line-clamp-1">
                    {gif ? gifDisplayTitle(gif) : '(unknown)'}
                  </p>
                  <p className="text-[10px] font-mono text-default-400 truncate">
                    {id}
                  </p>
                  {isDead && (
                    <Chip
                      size="sm"
                      color="danger"
                      variant="flat"
                      className="mt-1 text-[10px]"
                    >
                      missing from library
                    </Chip>
                  )}
                  {isGold && (
                    <Chip
                      size="sm"
                      color="warning"
                      variant="flat"
                      className="mt-1 text-[10px]"
                    >
                      ★ gold (hard)
                    </Chip>
                  )}
                </div>
                {/* Star toggle lives on every `good` row so the curator can
                    promote/demote gold from the bucket list itself. */}
                {bucket === 'good' && (
                  <Button
                    size="sm"
                    variant={isGold ? 'solid' : 'light'}
                    color="warning"
                    isIconOnly
                    onPress={() => toggleGold(id)}
                    title="Toggle gold — you'd be disappointed not to see this"
                  >
                    {isGold ? '★' : '☆'}
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="light"
                  isIconOnly
                  onPress={() => removeFromBuckets(id)}
                >
                  ✕
                </Button>
              </div>
            )
          })
        )}
      </CardBody>
    </Card>
  )

  const renderBuilder = () => (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button size="sm" variant="flat" onPress={handleBackToLibrary}>
            ← Library
          </Button>
          <span className="text-sm text-default-500">
            {isNew ? 'New fixture' : `Editing ${editingSlug}`}
          </span>
          {isDirty && (
            <Chip size="sm" color="warning" variant="flat">
              unsaved changes
            </Chip>
          )}
          {!isDirty && savedSnapshot !== null && !isNew && (
            <Chip size="sm" color="success" variant="flat">
              saved
            </Chip>
          )}
          {resolvingIds && (
            <span className="text-xs text-default-400 flex items-center gap-1">
              <Spinner size="sm" /> checking ids…
            </span>
          )}
          {deadIds.size > 0 && (
            <Chip size="sm" color="danger" variant="flat">
              {deadIds.size} dead id{deadIds.size === 1 ? '' : 's'}
            </Chip>
          )}
        </div>
        <div className="flex gap-2">
          <Button
            color="primary"
            onPress={() => void handleSave()}
            isLoading={saving}
            isDisabled={!form.note.trim()}
          >
            Save to disk
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 xl:items-start">
        {/* LEFT: scenario → explore → secondary tools → fixture — spans 2/3 */}
        <div className="xl:col-span-2 space-y-6">
          {/* STEP 1 — Scenario: the only hand-authored inputs up front. */}
          <Card>
            <CardHeader className="flex items-center gap-3">
              <StepBadge n={1} done={scenarioSet} />
              <div className="min-w-0">
                <h2 className="text-lg font-semibold">Scenario</h2>
                <p className="text-xs text-default-500">
                  Frame the moment — the only thing you write by hand.
                  Everything below reacts to it.
                </p>
              </div>
            </CardHeader>
            <CardBody className="space-y-4">
              <Textarea
                label="Conversation snippet"
                placeholder={"friend 1: hello\nme: hey, what's up"}
                value={form.conversationSnippet}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    conversationSnippet: e.target.value,
                  }))
                }
                minRows={3}
              />
              <Textarea
                label="User intent (optional)"
                placeholder="what the user wants to convey"
                value={form.userIntent}
                onChange={(e) =>
                  setForm((f) => ({ ...f, userIntent: e.target.value }))
                }
                minRows={2}
              />
              <Select
                label="Recommender preset"
                size="sm"
                selectedKeys={[preset]}
                onSelectionChange={(keys) => {
                  const key = Array.from(keys)[0]
                  if (typeof key === 'string') setPreset(key)
                }}
                description="Drives Explore & react and Run recommender below."
                className="max-w-xs"
              >
                {PRESET_NAMES.map((name) => (
                  <SelectItem key={name}>{name}</SelectItem>
                ))}
              </Select>
            </CardBody>
          </Card>

          {/* STEP 2 — Gather signal. Explore & react is the primary surface. */}
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <StepBadge n={2} done={bucketTotal > 0} />
              <div className="min-w-0">
                <h2 className="text-lg font-semibold">Gather signal</h2>
                <p className="text-xs text-default-500">
                  React to concrete candidates; bucket the traps and forbidden
                  picks.
                </p>
              </div>
            </div>

            <FixtureReactionPass
              conversationSnippet={form.conversationSnippet}
              userIntent={form.userIntent}
              preset={preset}
              gifById={gifById}
              hasNote={!!form.note.trim() && !draftFields.has('note')}
              drafting={drafting}
              onAssignBucket={assignToBucket}
              onRemoveBucket={removeFromBuckets}
              onDraft={(reactions, stanceReactions) =>
                runDraft({ reactions, stanceReactions })
              }
            />

            {/* Secondary manual tools — quiet until expanded. */}
            <Accordion
              variant="bordered"
              selectionMode="multiple"
              className="px-0"
            >
              <AccordionItem
                key="recommender"
                aria-label="Run recommender"
                title={
                  <span className="text-sm font-medium">Run recommender</span>
                }
                subtitle={
                  <span className="text-xs text-default-500">
                    See the live board and bucket picks directly
                  </span>
                }
              >
                <div className="space-y-3 pb-2">
                  <div className="flex justify-end">
                    <Button
                      color="primary"
                      size="sm"
                      className="shrink-0 whitespace-nowrap"
                      onPress={handleRunRecommender}
                      isLoading={recommending}
                      isDisabled={
                        recommending ||
                        (!form.conversationSnippet.trim() &&
                          !form.userIntent.trim())
                      }
                    >
                      Run against this conversation
                    </Button>
                  </div>
                  {recommendError && (
                    <div className="p-3 bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-300 rounded text-sm">
                      {recommendError}
                    </div>
                  )}
                  {!recommendError && !recommendations && !recommending && (
                    <p className="text-sm text-default-500">
                      Optional. Run the live recommender against the
                      conversation to see what it picks; tag any
                      tempting-but-wrong picks into <strong>traps</strong> or
                      never-acceptable ones into <strong>forbidden</strong>{' '}
                      directly.
                    </p>
                  )}
                  {recommendations && (
                    <div className="space-y-3">
                      {recommendations
                        .slice()
                        .sort((a, b) => a.rank - b.rank)
                        .map((rec) => {
                          const gif = gifById.get(rec.gifId)
                          return (
                            <div
                              key={rec.gifId}
                              className="p-3 bg-default-50 dark:bg-default-100/10 rounded-lg"
                            >
                              <div className="flex gap-3">
                                {gif ? (
                                  <GifThumbnail
                                    gif={gif}
                                    className="w-20 h-20 rounded-lg flex-shrink-0"
                                  />
                                ) : (
                                  <div className="w-20 h-20 rounded bg-default-200 flex-shrink-0" />
                                )}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <Chip
                                      size="sm"
                                      variant="flat"
                                      color="primary"
                                    >
                                      Rank {rec.rank}
                                    </Chip>
                                  </div>
                                  {gif && (
                                    <p className="text-sm font-medium mt-1 line-clamp-1">
                                      {gifDisplayTitle(gif)}
                                    </p>
                                  )}
                                  <p className="text-xs text-default-500 mt-1 line-clamp-2">
                                    {rec.reasoning}
                                  </p>
                                  <p className="font-mono text-[10px] text-default-400 mt-1 truncate">
                                    {rec.gifId}
                                  </p>
                                </div>
                              </div>
                              {renderBucketButtons(rec.gifId)}
                            </div>
                          )
                        })}
                    </div>
                  )}
                </div>
              </AccordionItem>

              <AccordionItem
                key="library"
                aria-label="Browse library"
                title={
                  <span className="text-sm font-medium">Browse library</span>
                }
                subtitle={
                  <span className="text-xs text-default-500">
                    Search the collection and bucket by hand
                  </span>
                }
              >
                <div className="space-y-3 pb-2">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-xs text-default-400">
                      {filteredGifs.length}
                      {filteredGifs.length !== gifs.length
                        ? ` of ${gifs.length}`
                        : ''}{' '}
                      gifs
                    </span>
                  </div>
                  <Input
                    placeholder="Search description, source, keywords, emotion, tags…"
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value)
                      setBrowseLimit(BROWSE_INITIAL)
                    }}
                    isClearable
                    onClear={() => {
                      setSearchQuery('')
                      setBrowseLimit(BROWSE_INITIAL)
                    }}
                  />
                  {gifsLoading && (
                    <div className="flex justify-center py-8">
                      <Spinner />
                    </div>
                  )}
                  {gifsError && (
                    <div className="p-3 bg-red-50 text-red-700 rounded text-sm">
                      Error loading GIFs: {gifsError}
                    </div>
                  )}
                  {!gifsLoading && !gifsError && (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {visibleGifs.map(renderBrowseCard)}
                      </div>
                      {visibleGifs.length < filteredGifs.length && (
                        <div className="flex justify-center mt-4">
                          <Button
                            variant="flat"
                            onPress={() =>
                              setBrowseLimit((n) => n + BROWSE_PAGE)
                            }
                          >
                            Load{' '}
                            {Math.min(
                              BROWSE_PAGE,
                              filteredGifs.length - visibleGifs.length
                            )}{' '}
                            more
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </AccordionItem>
            </Accordion>
          </div>

          {/* STEP 3 — The fixture: Draft, then approve. The note is the product. */}
          <Card className="border-2 border-primary-200 dark:border-primary-900">
            <CardHeader className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <StepBadge n={3} done={noteApproved} />
                <div className="min-w-0">
                  <h2 className="text-lg font-semibold">Fixture</h2>
                  <p className="text-xs text-default-500">
                    Draft, then edit each field to approve.
                  </p>
                </div>
              </div>
              <Button
                color="secondary"
                className="shrink-0 whitespace-nowrap"
                onPress={() => void runDraft()}
                isLoading={drafting}
                isDisabled={
                  drafting ||
                  (!form.conversationSnippet.trim() && !form.userIntent.trim())
                }
              >
                ✨ Draft fixture
              </Button>
            </CardHeader>
            <CardBody className="space-y-5">
              {/* The NOTE — the load-bearing artifact, given the most weight. */}
              <div className="rounded-lg border-2 border-primary-300 dark:border-primary-800 bg-primary-50/40 dark:bg-primary-950/20 p-4">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                  <span className="text-base font-semibold">
                    Note — the viable response moves
                  </span>
                  {draftFields.has('note') ? (
                    <Chip size="sm" color="secondary" variant="flat">
                      draft — edit to approve
                    </Chip>
                  ) : noteApproved ? (
                    <Chip size="sm" color="success" variant="flat">
                      approved
                    </Chip>
                  ) : null}
                </div>
                <Textarea
                  id="fixture-note-textarea"
                  aria-label="Note — the viable response moves"
                  placeholder={
                    'e.g. Viable moves: quiet smug vindication, a theatrical "I called it" gloat, or a warm "finally!" tease. Wrong: shock or surprise (the user predicted this); gloating so hard it reads as mean.'
                  }
                  value={form.note}
                  onChange={(e) => {
                    // First manual keystroke clears the draft flag — the human
                    // edit is what makes the note authoritative.
                    clearDraftField('note')
                    setForm((f) => ({ ...f, note: e.target.value }))
                  }}
                  minRows={7}
                  description="Describe the viable moves and what would be wrong — the judge scores boards against this. Load-bearing: this is fed to the rubric judge verbatim. Bucket gifs + Draft fixture to compile a draft."
                  isInvalid={!form.note.trim()}
                />
              </div>

              {/* Supporting metadata — smaller, below the note. */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Input
                    label="Slug"
                    placeholder="short-scenario-slug"
                    value={form.slug}
                    isDisabled={!isNew}
                    color={draftFields.has('slug') ? 'secondary' : 'default'}
                    description={
                      isNew ? undefined : 'Slug is fixed when editing a fixture'
                    }
                    onChange={(e) => {
                      clearDraftField('slug')
                      setForm((f) => ({ ...f, slug: e.target.value }))
                    }}
                  />
                  {draftFields.has('slug') && (
                    <Chip
                      size="sm"
                      color="secondary"
                      variant="flat"
                      className="mt-1"
                    >
                      draft — edit to approve
                    </Chip>
                  )}
                </div>
                <div>
                  <Input
                    label="Description"
                    placeholder="What does this fixture cover?"
                    value={form.description}
                    color={
                      draftFields.has('description') ? 'secondary' : 'default'
                    }
                    onChange={(e) => {
                      clearDraftField('description')
                      setForm((f) => ({ ...f, description: e.target.value }))
                    }}
                  />
                  {draftFields.has('description') && (
                    <Chip
                      size="sm"
                      color="secondary"
                      variant="flat"
                      className="mt-1"
                    >
                      draft — edit to approve
                    </Chip>
                  )}
                </div>
              </div>
              <div>
                <Input
                  label="Tags (Enter/comma to add)"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={handleAddTag}
                />
                <div className="flex flex-wrap gap-1 mt-2 items-center">
                  {draftFields.has('tags') && form.tags.length > 0 && (
                    <Chip size="sm" color="secondary" variant="flat">
                      draft — edit to approve
                    </Chip>
                  )}
                  {form.tags.map((t) => (
                    <Chip
                      key={t}
                      onClose={() => {
                        clearDraftField('tags')
                        setForm((f) => ({
                          ...f,
                          tags: f.tags.filter((x) => x !== t),
                        }))
                      }}
                      variant="flat"
                    >
                      {t}
                    </Chip>
                  ))}
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        {/* RIGHT: sticky progress + buckets + save/export. Padding on the scroll
            container keeps card shadows from clipping against the edges. */}
        <div className="xl:sticky xl:top-4 xl:self-start xl:max-h-[calc(100vh-2rem)] xl:overflow-y-auto xl:-mx-1 xl:px-1">
          <div className="space-y-4 py-0.5">
            {/* Compact workflow progress readout. */}
            <Card>
              <CardBody className="space-y-2.5 py-3">
                <div className="flex items-center gap-2">
                  <StepBadge n={1} done={scenarioSet} />
                  <span className="text-sm">
                    Scenario{' '}
                    <span className="text-default-400">
                      {scenarioSet ? 'set' : 'empty'}
                    </span>
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <StepBadge n={2} done={bucketTotal > 0} />
                  <span className="text-sm">
                    Signal{' '}
                    <span className="text-default-400">
                      {bucketTotal > 0
                        ? `${trapIds.length} trap · ${forbiddenIds.length} forbidden · ${goodIds.length} good${goldIds.length > 0 ? ` (${goldIds.length} ★ gold)` : ''}`
                        : 'none yet'}
                    </span>
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <StepBadge n={3} done={noteApproved} />
                  <span className="text-sm">
                    Note{' '}
                    <span className="text-default-400">
                      {!noteDrafted
                        ? 'unwritten'
                        : draftFields.has('note')
                          ? 'drafted → edit'
                          : 'approved'}
                    </span>
                  </span>
                </div>
              </CardBody>
            </Card>

            {BUCKETS.map((b) =>
              renderBucketColumn(
                b,
                b === 'traps'
                  ? trapIds
                  : b === 'forbidden'
                    ? forbiddenIds
                    : goodIds
              )
            )}

            <Card>
              <CardHeader>
                <h3 className="font-semibold text-sm">Save &amp; export</h3>
              </CardHeader>
              <CardBody className="space-y-2">
                <Button
                  color="primary"
                  onPress={() => void handleSave()}
                  isLoading={saving}
                  className="w-full"
                  isDisabled={!form.note.trim()}
                >
                  Save to disk
                </Button>
                <p className="text-[11px] text-default-500">
                  Writes{' '}
                  <code className="text-[10px]">
                    fixtures/{(editingSlug ?? form.slug) || '<slug>'}/meta.json
                  </code>{' '}
                  via the dev-server bridge.
                </p>
                <div className="pt-2 border-t border-default-200 dark:border-default-100/20 space-y-2">
                  <p className="text-[11px] text-default-400">
                    Fallback (no dev server):
                  </p>
                  <Button
                    variant="flat"
                    onPress={handleDownloadJson}
                    className="w-full"
                    isDisabled={!form.note.trim()}
                  >
                    Download meta.json
                  </Button>
                  <Button
                    variant="flat"
                    onPress={handleCopyJson}
                    className="w-full"
                  >
                    Copy JSON to clipboard
                  </Button>
                </div>
              </CardBody>
            </Card>
          </div>
        </div>
      </div>
    </div>
  )

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Eval Fixtures</h1>
        <p className="text-sm text-default-500 mt-1">
          Open an existing recommendation-eval fixture to edit it, author a new
          one, or triage the unlabeled picks from an eval run. See{' '}
          <code>docs/architecture/recommendations-eval.md</code>.
        </p>
      </div>

      <Tabs
        aria-label="Eval fixture tools"
        selectedKey={
          view === 'triage'
            ? 'triage'
            : view === 'calibration'
              ? 'calibration'
              : 'author'
        }
        onSelectionChange={(key) => {
          // Leaving the builder with unsaved edits needs a confirm; the other
          // tabs are read/label surfaces that don't hold builder draft state.
          if (
            view === 'builder' &&
            isDirty &&
            key !== 'author' &&
            !confirm(`Discard unsaved changes and switch to ${String(key)}?`)
          ) {
            return
          }
          if (key === 'triage') setView('triage')
          else if (key === 'calibration') setView('calibration')
          else setView('library')
        }}
      >
        <Tab key="author" title="Author">
          {view === 'builder' ? (
            renderBuilder()
          ) : (
            <FixtureLibrary
              onOpen={loadFixtureIntoBuilder}
              onCreateNew={startNewFixture}
            />
          )}
        </Tab>
        <Tab key="triage" title="Triage inbox">
          <FixtureTriageInbox gifById={gifById} />
        </Tab>
        <Tab key="calibration" title="Judge calibration">
          <JudgeCalibration gifById={gifById} />
        </Tab>
      </Tabs>
    </div>
  )
}
